import { useEffect, useState } from "react"
import { Link } from "react-router"
import {
  fetchMeScans,
  disclaimQrCode,
  acknowledgeClaims,
  openBillingPortal,
  updateNotificationSettings,
} from "../lib/api"
import useUser from "../hooks/useUser"
import useUpgrade from "../hooks/useUpgrade"
import { browserTimeZone } from "../lib/timezone"
import QrBuilder from "./QrBuilder"
import StatCards from "./dashboard/StatCards"
import YourCodesCard from "./dashboard/YourCodesCard"
import SevenDayChart from "./dashboard/SevenDayChart"
import RecentScansFeed from "./dashboard/RecentScansFeed"
import PlanCard from "./dashboard/PlanCard"
import CleanCopyNotice from "./dashboard/CleanCopyNotice"

// Strip the ?checkout=success|cancelled and ?code=<token> params the Stripe
// return URL writes, after we read them, so a refresh doesn't re-trigger the
// notice or re-open the editor. `code` is the deep-link an anonymous hero
// upgrade carries home (see BillingController): open that code's editor so the
// visitor lands back where the Pro lock stopped them.
function consumeReturnParams() {
  const params = new URLSearchParams(window.location.search)
  const checkout = params.get("checkout")
  const code = params.get("code")
  if (!checkout && !code) return { checkout: null, code: null }
  params.delete("checkout")
  params.delete("code")
  const qs = params.toString()
  window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""))
  return { checkout, code }
}

// useUser caches /me for the life of the page, so its notification flags go
// stale after the dashboard acts on them. These remember, for the same life,
// that the time zone was sent and the digest notice dismissed, so a trip to
// Profile and back neither re-sends nor re-shows.
let timezoneSent = false
let digestNoticeDismissed = false

export default function Dashboard() {
  const { user } = useUser()
  // Opens the pricing box; the box starts the checkout (#336).
  const { upgrade: handleUpgrade } = useUpgrade()
  const isPaid = !!user?.entitlements?.paid
  const isSuperuser = !!user?.entitlements?.superuser
  const subStatus = user?.subscription?.status
  const isPastDue = subStatus === "past_due" || subStatus === "unpaid"

  const [scansSummary, setScansSummary]   = useState(null) // { total_lifetime, code_count, daily, week_total, prev_week_total, top_code }
  const [codesPayload, setCodesPayload]   = useState(null) // last response from YourCodesCard
  const [newCode, setNewCode]             = useState(null)
  const [highlight, setHighlight]         = useState(null) // { token, at }: the code Done just handed to the list
  const [billingBusy, setBillingBusy]     = useState(false)
  const [checkoutNotice, setCheckoutNotice] = useState(null)
  const [openEditToken, setOpenEditToken] = useState(null)
  const [error, setError]                 = useState(null)

  // The weekly scan summary (#304) comes on Monday morning local time, so the
  // server asks for the browser's time zone once, when it has none. Best
  // effort: without it the summary uses a fixed UTC hour. A staff view-as
  // session is refused the write, which is fine.
  useEffect(() => {
    if (!user?.notifications?.timezone_missing || timezoneSent) return
    const zone = browserTimeZone()
    if (!zone) return
    timezoneSent = true
    updateNotificationSettings({ timezone: zone }).catch(() => {})
  }, [user?.notifications?.timezone_missing])

  const [digestNotice, setDigestNotice] = useState(!digestNoticeDismissed)

  async function dismissDigestNotice() {
    digestNoticeDismissed = true
    setDigestNotice(false)
    try {
      await updateNotificationSettings({ digest_notice_dismissed: true })
    } catch {
      // The card is gone for now. It comes back on the next load, which is
      // the honest result of a save that didn't land.
    }
  }

  useEffect(() => {
    const { checkout, code } = consumeReturnParams()
    setCheckoutNotice(checkout)
    setOpenEditToken(code)
  }, [])

  function loadScans() {
    return fetchMeScans()
      .then((data) => setScansSummary(data))
      .catch(() => {}) // non-fatal; widgets render their own loading states
  }

  useEffect(() => {
    let cancelled = false
    fetchMeScans()
      .then((data) => { if (!cancelled) setScansSummary(data) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // A new code changes the stat cards (Active codes, "across N codes"), so
  // re-read them when one is created (#316). Restyles publish the same token
  // through onCreated and do not refetch.
  useEffect(() => {
    if (newCode?.token) loadScans()
  }, [newCode?.token])

  // Both return whether the request succeeded, so YourCodesCard can update its
  // own list: the "Recently saved" box it renders must change the moment a
  // button works, not on the next reload (#317).
  async function handleDisclaim(token) {
    try {
      await disclaimQrCode(token)
      if (newCode?.token === token) setNewCode(null)
      loadScans()
      return true
    } catch (e) {
      setError(e.message || "Couldn't remove that code. Try again.")
      return false
    }
  }

  async function handleAcknowledgeAll() {
    try {
      await acknowledgeClaims()
      return true
    } catch (e) {
      setError(e.message || "Couldn't dismiss the section. Try again.")
      return false
    }
  }

  async function handleManageBilling() {
    setBillingBusy(true)
    try {
      await openBillingPortal()
    } catch (e) {
      setBillingBusy(false)
      setError(e.message || "Couldn't open the billing portal. Try again.")
    }
  }

  const accountHasScans = (scansSummary?.total_lifetime || 0) > 0

  return (
    <div className="max-w-[1180px] mx-auto py-6 px-4">
      {checkoutNotice === "success" && (
        <NoticeBanner tone="success" body="Thanks! Your upgrade is processing. Reload in a moment to see your paid features.">
          <button
            type="button"
            onClick={() => window.location.assign("/app")}
            className="bg-voltage text-coal rounded px-3 py-1.5 text-xs font-medium hover:bg-voltage-dark transition flex-shrink-0"
          >
            Reload
          </button>
        </NoticeBanner>
      )}
      {checkoutNotice === "cancelled" && (
        <NoticeBanner body="Checkout cancelled. You're still on the free plan." />
      )}
      {user && isPastDue && (
        <NoticeBanner tone="fail" title="Your last payment failed" body="Update your card to keep Pro. We're still retrying for a few more days.">
          <button
            type="button"
            onClick={handleManageBilling}
            disabled={billingBusy}
            className="border border-fail text-fail rounded-lg px-3 py-2 text-sm font-medium hover:bg-fail/10 transition disabled:opacity-50 flex-shrink-0"
          >
            {billingBusy ? "Opening…" : "Manage billing"}
          </button>
        </NoticeBanner>
      )}
      {error && (
        <div className="border border-fail rounded p-3 text-fail text-sm mb-4">
          {error}
        </div>
      )}

      {/* Stat-first stack: KPI row → create bar → Your codes → widget band. */}
      <div className="flex flex-col gap-4">
        <StatCards scansSummary={scansSummary} loading={!scansSummary} />

        {user?.notifications?.digest_notice && digestNotice && (
          <DigestNotice onDismiss={dismissDigestNotice} />
        )}

        {user?.wordmark_notice && <CleanCopyNotice />}

        <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg p-6">
          <QrBuilder
            layout="dashboard"
            onCreated={(code) => setNewCode(code)}
            // Done (#316): the builder clears for the next code and the list
            // shows where this one went. `at` re-fires the same token.
            onDone={(code) => code && setHighlight({ token: code.token, at: Date.now() })}
            onUpgrade={handleUpgrade}
            isPaid={isPaid}
            // Dashboard is auth-gated, and the server already skips
            // honeypot/timing/Turnstile validation for signed-in callers
            // (see Api::V1::QrCodesController#anonymous?). Rendering the
            // Turnstile widget here just fetches Cloudflare and shows a
            // challenge box on the page for no functional benefit.
            requireBotProtection={false}
            // Making a hosted contact page (#332), per user from /api/v1/me.
            contactPages={!!user?.features?.contact_pages}
          />
        </div>

        <YourCodesCard
          isPaid={isPaid}
          bulkRepoint={!!user?.features?.bulk_repoint}
          bulkCreate={!!user?.features?.bulk_create}
          contactPages={!!user?.features?.contact_pages}
          onCodesLoaded={setCodesPayload}
          onDisclaim={handleDisclaim}
          onAcknowledgeAll={handleAcknowledgeAll}
          onUpgrade={handleUpgrade}
          onCodeUpdated={() => loadScans()}
          // Create many, moves and repoints reload the list, not the builder,
          // so the stat cards (Active codes, Top code) re-read here (#327).
          onLibraryChanged={() => loadScans()}
          newCode={newCode}
          highlight={highlight}
          openEditToken={openEditToken}
        />

        {/* Widget band: 3 cards across, collapses to 1 under 920px. */}
        <div className="grid grid-cols-1 min-[920px]:grid-cols-3 gap-4">
          <RecentScansFeed accountHasScans={accountHasScans} />
          <SevenDayChart daily={scansSummary?.daily || []} loading={!scansSummary} />
          <PlanCard
            codes={codesPayload?.codes || []}
            isPaid={isPaid}
            busy={billingBusy}
            onUpgrade={handleUpgrade}
            onManageBilling={handleManageBilling}
          />
        </div>
      </div>
    </div>
  )
}

// The weekly scan summary is on by default (#304, decision 2), so the owner
// hears about it here before the first one comes. A notice, not a prompt:
// it states what happens and where to turn it off. No pricing on it. The
// dismissal is saved on the account, so it stays gone on every device.
function DigestNotice({ onDismiss }) {
  return (
    <div className="border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight rounded-lg p-4 flex items-start justify-between gap-4">
      <p className="text-sm text-ink dark:text-cloud">
        Each Monday we email you the scans on your codes.{" "}
        <Link to="/app/profile" className="text-voltage-dark dark:text-voltage-light font-medium hover:underline">
          Turn it off in Profile
        </Link>
        .
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud p-1 bg-transparent border-0 cursor-pointer flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voltage rounded"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  )
}

// Generic notice banner — checkout / past-due / cancelled all use the same
// frame. `tone` switches border color; default is voltage.
function NoticeBanner({ tone = "voltage", title, body, children }) {
  const border =
    tone === "fail"    ? "border-fail/50" :
    tone === "success" ? "border-voltage/40" :
                         "border-[color:var(--color-border)] dark:border-storm"
  return (
    <div className={`border ${border} bg-[color:var(--color-cloud)] dark:bg-coal rounded-lg p-4 mb-4 flex items-center justify-between gap-4`}>
      <div>
        {title && <div className={`font-display font-semibold text-sm ${tone === "fail" ? "text-fail" : "text-ink dark:text-cloud"}`}>{title}</div>}
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-0.5">{body}</p>
      </div>
      {children}
    </div>
  )
}
